import React, { useEffect, useMemo, useState } from 'react';
import Select from 'react-select';
import { CgSpinner } from 'react-icons/cg';
import { BookingQuestionsModal } from '../common/modals';
import { getBookingTsTransferOptions } from './bookingstsApi';
import './bookingstsTransferModal.css';

/**
 * Confirmation modal used to transfer an existing booking to another room.
 * Loads valid destination rooms for the exact booking timeslot.
 */
export default function BookingstsTransferModal({
    modalActive,
    setModalActive,
    roomGuid,
    bookingGuid,
    currentRoomName,
    date,
    startTime,
    endTime,
    onSubmit,
}) {
    const [loadingOptions, setLoadingOptions] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [options, setOptions] = useState([]);
    const [selectedRoom, setSelectedRoom] = useState(null);

    useEffect(() => {
        if (!modalActive) {
            setLoadingOptions(false);
            setSubmitting(false);
            setOptions([]);
            setSelectedRoom(null);
            return;
        }

        if (!date || !roomGuid || !bookingGuid) {
            setOptions([]);
            return;
        }

        setLoadingOptions(true);
        (async () => {
            try {
                const response = await getBookingTsTransferOptions({
                    date,
                    roomGuid,
                    bookingGuid,
                });
                const items = Array.isArray(response?.items) ? response.items : [];
                const mappedOptions = items
                    .map((item) => {
                        const value = String(item?.id || item?.roomId || '').trim();
                        const label = String(item?.room || item?.name || '').trim();
                        if (!value || !label) {
                            return null;
                        }
                        return { value, label };
                    })
                    .filter(Boolean);
                setOptions(mappedOptions);
            } catch (error) {
                console.error('failed to load transfer rooms', error);
                setOptions([]);
            } finally {
                setLoadingOptions(false);
            }
        })();
    }, [modalActive, date, roomGuid, bookingGuid]);

    const noOptionsMessage = useMemo(() => {
        if (loadingOptions) {
            return () => 'Loading available rooms...';
        }
        return () => 'No available rooms for this timeslot';
    }, [loadingOptions]);

    async function handleSubmit() {
        if (!selectedRoom) {
            return;
        }

        setSubmitting(true);
        try {
            await onSubmit({
                targetRoomGuid: selectedRoom.value,
                targetRoomName: selectedRoom.label,
            });
            setModalActive(false);
        } finally {
            setSubmitting(false);
        }
    }

    return (
        <BookingQuestionsModal modalActiveState={modalActive} setStateFunction={setModalActive}>
            <div className='bookingstsTransferContainer'>
                <div className='bookingstsTransferDetails'>
                    <p>Transfer booking</p>
                    <p>{currentRoomName}</p>
                    <p>{date} {startTime} - {endTime}</p>
                </div>

                <div className='bookingstsTransferField'>
                    <label htmlFor='bookingstsTransferRoom'>Transfer to room</label>
                    <Select
                        inputId='bookingstsTransferRoom'
                        classNamePrefix='bookingstsTransferSelect'
                        isDisabled={loadingOptions || submitting}
                        options={options}
                        value={selectedRoom}
                        onChange={setSelectedRoom}
                        placeholder={loadingOptions ? 'Loading Available Rooms' : 'Select available room'}
                        noOptionsMessage={noOptionsMessage}
                    />
                    <p className='bookingstsTransferNote'>All attendees will be notified of the change via email.</p>
                </div>

                <div className='bookingstsTransferSubmitArea'>
                    <button
                        className='formSubmitButton'
                        type='button'
                        onClick={handleSubmit}
                        disabled={!selectedRoom || submitting || loadingOptions}
                    >
                        {submitting ? <CgSpinner className='submit-button-spinner' /> : 'Transfer booking'}
                    </button>
                </div>
            </div>
        </BookingQuestionsModal>
    );
}
